/**
 * Webhook payload display helpers for customer portal reports.
 *
 * This module keeps customer-facing webhook payload selection logic isolated
 * from the report component. Message-aligned job rows store internal metadata
 * (`sourceData`, `target`, `confirmation`) while delivery attempts persist the
 * exact customer payload body (`customerPayloadJson`).
 */

import type { WebhookAuditReportItem, WebhookJobAttempt } from '../types/portal';

const INTERNAL_WEBHOOK_PAYLOAD_KEYS = new Set([
  'deliveryTarget',
  'confirmation',
  'accountId',
  'shardName',
  'sourceData',
  'target'
]);
const CUSTOMER_WEBHOOK_TOP_LEVEL_KEYS = ['SchemaVersion', 'EventType', 'EventOccurredAt', 'Data'] as const;

function readStringProperty(container: unknown, key: string): string | null {
  if (!container || typeof container !== 'object' || Array.isArray(container)) {
    return null;
  }
  const value = (container as Record<string, unknown>)[key];
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

function readObjectProperty(container: unknown, key: string): Record<string, unknown> | null {
  if (!container || typeof container !== 'object' || Array.isArray(container)) {
    return null;
  }
  const value = (container as Record<string, unknown>)[key];
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

function parseAttemptCustomerPayload(customerPayloadJson: string | null | undefined): Record<string, unknown> | null {
  if (typeof customerPayloadJson !== 'string' || customerPayloadJson.trim().length === 0) {
    return null;
  }
  try {
    const parsed = JSON.parse(customerPayloadJson) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return null;
    }
    return parsed as Record<string, unknown>;
  } catch {
    return null;
  }
}

function selectLatestAttemptPayload(attempts: WebhookJobAttempt[]): Record<string, unknown> | null {
  if (!Array.isArray(attempts) || attempts.length === 0) {
    return null;
  }
  const ordered = [...attempts].sort((left, right) => {
    if (left.attemptNumber !== right.attemptNumber) {
      return right.attemptNumber - left.attemptNumber;
    }
    return Date.parse(right.attemptedAt) - Date.parse(left.attemptedAt);
  });
  for (const attempt of ordered) {
    if (!attempt.isTerminal) {
      continue;
    }
    const parsed = parseAttemptCustomerPayload(attempt.customerPayloadJson);
    if (parsed) {
      return parsed;
    }
  }
  for (const attempt of ordered) {
    const parsed = parseAttemptCustomerPayload(attempt.customerPayloadJson);
    if (parsed) {
      return parsed;
    }
  }
  return null;
}

function sanitizeLegacyCustomerWebhookPayload(payload: Record<string, unknown>): Record<string, unknown> | null {
  const stripped = Object.fromEntries(
    Object.entries(payload).filter(([key]) => !INTERNAL_WEBHOOK_PAYLOAD_KEYS.has(key))
  );
  const hasCustomerSchemaField = CUSTOMER_WEBHOOK_TOP_LEVEL_KEYS.some((key) =>
    Object.prototype.hasOwnProperty.call(stripped, key)
  );
  if (!hasCustomerSchemaField) {
    return null;
  }
  const customerFacing: Record<string, unknown> = {};
  for (const key of CUSTOMER_WEBHOOK_TOP_LEVEL_KEYS) {
    if (Object.prototype.hasOwnProperty.call(stripped, key)) {
      customerFacing[key] = stripped[key];
    }
  }
  return customerFacing;
}

export function selectDisplayedWebhookPayload(
  job: WebhookAuditReportItem | null,
  attempts: WebhookJobAttempt[]
): Record<string, unknown> | null {
  const fromAttempts = selectLatestAttemptPayload(attempts);
  if (fromAttempts) {
    return fromAttempts;
  }
  if (!job) {
    return null;
  }
  return sanitizeLegacyCustomerWebhookPayload(job.payload ?? {});
}

export function resolveParentMessageId(job: WebhookAuditReportItem | null): string | null {
  if (!job) {
    return null;
  }
  const sourceData = readObjectProperty(job.payload, 'sourceData');
  const nestedData = readObjectProperty(job.payload, 'Data');
  return (
    job.messageId ??
    readStringProperty(sourceData, 'prontoMessageId') ??
    readStringProperty(sourceData, 'messageId') ??
    readStringProperty(job.payload, 'prontoMessageId') ??
    readStringProperty(job.payload, 'messageId') ??
    readStringProperty(job.payload, 'triggerMessageId') ??
    readStringProperty(nestedData, 'ProntoMessageId') ??
    readStringProperty(nestedData, 'MessageId') ??
    null
  );
}

export function resolveParentMessageType(job: WebhookAuditReportItem | null): 'SMS' | 'MMS' {
  if (!job) {
    return 'SMS';
  }
  const sourceData = readObjectProperty(job.payload, 'sourceData');
  const nestedData = readObjectProperty(job.payload, 'Data');
  const fromTopLevel = readStringProperty(job.payload, 'messageType') ?? readStringProperty(job.payload, 'MessageType');
  const fromSourceData = readStringProperty(sourceData, 'messageType') ?? readStringProperty(sourceData, 'MessageType');
  const fromNested = readStringProperty(nestedData, 'MessageType') ?? readStringProperty(nestedData, 'messageType');
  const normalized = (fromTopLevel ?? fromSourceData ?? fromNested ?? 'SMS').toUpperCase();
  return normalized === 'MMS' ? 'MMS' : 'SMS';
}
